import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import { toastMessage } from "shared/components/common/toast";
import ReviewStep from "shared/components/questionnaire/review";
import QuestionnaireStep1 from "shared/components/questionnaire/step1";
import QuestionnaireStep10 from "shared/components/questionnaire/step10";
import QuestionnaireStep2 from "shared/components/questionnaire/step2";
import QuestionnaireStep3 from "shared/components/questionnaire/step3";
import QuestionnaireStep4 from "shared/components/questionnaire/step4";
import QuestionnaireStep5 from "shared/components/questionnaire/step5";
import QuestionnaireStep6 from "shared/components/questionnaire/step6";
import QuestionnaireStep7 from "shared/components/questionnaire/step7";
import QuestionnaireStep8 from "shared/components/questionnaire/step8";
import QuestionnaireStep9 from "shared/components/questionnaire/step9";
import { setBreadCrumb } from "shared/redux/reducers/breadCrumbSlice";
import { routeConstant } from "shared/routes/routeConstant";

import {
  AddDraftEvaluationsService,
  CompletePendingEvaluationService,
  GetSinglePendingEvalutionService,
} from "shared/services/evaluations";
import {
  checkDropdownOptions,
  checkIfComplete,
  checkIfWarning,
  getPercentagefromString,
  mergeArrays,
} from "shared/utils/helper";
import styles from "./style.module.scss";
import { JobRole } from "shared/utils/enum";
import SuccessModal from "shared/modal/success";
import {
  GetAppOutcomesService,
  GetQuestionsService,
} from "shared/services/questionnaire";
import { setQuestionsReducer } from "shared/redux/reducers/questions";
import { setQuestionnaireReducer } from "shared/redux/reducers/questionnaire";
import ConfirmationModal from "shared/modal/confirmation";
import { GetCountService } from "shared/services/general";
import { setCountReducer } from "shared/redux/reducers/count";

const ContinueQuestionnaire = () => {
  const {
    questionnaire: { formData, lastStep, id },
  } = useSelector((state: any) => state?.root);

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formikRef = useRef<any>(null);
  const finalPayload = useRef<any>({});
  const finalDraftPayload = useRef<any>({});
  const loadingRef = useRef<boolean>(false);
  const stepRef = useRef<number>(1);
  const initialFormData = useRef<any[]>([]);

  const [step, setStep] = useState<number>(1);
  const [localFormData, setLocalFormData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [draftLoading, setDraftLoading] = useState<boolean>(false);
  const [evalsLoading, setEvalsLoading] = useState<boolean>(true);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleShowSuccessModal = () => {
    setShowSuccessModal(true);
  };

  const handleCloaseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate(routeConstant.requestedEvaluation.path, { replace: true });
  };

  const handleGetQuestions = () => {
    GetQuestionsService()
      .then(({ data }) => {
        dispatch(setQuestionsReducer({ questions: data }));
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const handleGetEvaluations = () => {
    setEvalsLoading(true);
    if (params?.id) {
      GetSinglePendingEvalutionService(String(params?.id))
        .then(({ data }) => {
          dispatch(
            setBreadCrumb({
              crumbs: [
                {
                  title:
                    routeConstant.continuePendingEvaluation.title +
                    ": " +
                    data?.organization_id?.name,
                },
              ],
            })
          );
          let tempObj: any = {
            jobTitle: data?.current_version?.jobTitle,
            department: data?.current_version?.department,
            roleType: {
              name:
                data?.current_version?.roleType === JobRole.management
                  ? "Manager"
                  : "Individual Contributor",
              value: data?.current_version?.roleType,
            },
            q1: null,
            q2: null,
            q3: null,
            q4: null,
            q5: null,
            q6: null,
            q7: null,
            q8: null,
            q9: null,
            shortCode: "",
            rasScore: 0,
            rasLevel: 0,
            jobCode: data?.jobCode ? data?.jobCode : "",
            jobGrade: data?.current_version?.jobGrade
              ? data?.current_version?.jobGrade
              : "",
          };

          initialFormData.current = [tempObj];
          if (String(params?.id) !== id) {
            setLocalFormData([tempObj]);
            dispatch(
              setQuestionnaireReducer({
                formData: [tempObj],
                lastStep: 1,
                id: String(params?.id),
              })
            );
          } else {
            setEvalsLoading(false);
            if (formData?.length > 0) {
              setLocalFormData(formData);
              setStep(lastStep);
            }
          }
        })
        .catch((err) => {
          navigate(-1);
          toastMessage("error", err?.response?.data?.error);
        })
        .finally(() => {
          setEvalsLoading(false);
        });
    } else {
      navigate(-1);
    }
  };

  const handleGetCount = () => {
    GetCountService()
      .then(({ data }) => {
        dispatch(
          setCountReducer({
            complaints: data?.complaints_count,
            orgs: data?.orgs_count,
            req: data?.req_count,
          })
        );
      })
      .catch((err) => {});
  };

  const handleSubmission = () => {
    setLoading(true);
    CompletePendingEvaluationService(
      finalPayload.current?.evals[0],
      String(params?.id)
    )
      .then(() => {
        dispatch(
          setQuestionnaireReducer({
            formData: [],
            lastStep: 1,
            id: "",
          })
        );
        handleGetCount();
        handleShowSuccessModal();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSetFormData = (finalArr: any) => {
    setLocalFormData(finalArr);
    dispatch(setQuestionnaireReducer({ formData: finalArr, lastStep: step }));
  };

  const handleDaftFormData = async (finalArr: any) => {
    setDraftLoading(true);
    let tempData: any[] = [];
    for (let i = 0; i < finalArr?.length; i++) {
      let job = finalArr[i];
      if (job?.roleType?.value === "1") {
        let tempObj: any = {
          jobTitle: job?.jobTitle,
          department: job?.department,
          roleType: job?.roleType?.value,
        };
        if (step >= 2 && step <= 10 && job?.q1) {
          if (job?.q1?.modifier?.value) {
            tempObj.modifier = job?.q1?.modifier?.value;
          }
          if (job?.q1?.levelWork?.value) {
            tempObj.levelOfWork = job?.q1?.levelWork?.value;
          }
        }
        if (step >= 3 && step <= 10 && job?.q2?.institutionalExpertise?.value) {
          tempObj.q2 = job?.q2?.institutionalExpertise?.value;
        }
        if (step >= 4 && step <= 10 && job?.q3?.culturalImpact?.value) {
          tempObj.q3 = job?.q3?.culturalImpact?.value;
        }
        if (step >= 5 && step <= 10 && job?.q4?.physicalEnvironment?.value) {
          tempObj.q4 = job?.q4?.physicalEnvironment?.value;
        }
        if (step >= 6 && step <= 10 && job?.q5?.mentalEnvironment?.value) {
          tempObj.q5 = job?.q5?.mentalEnvironment?.value;
        }
        if (step >= 7 && step <= 10 && job?.q6?.applicationLevel?.value) {
          tempObj.q6 = job?.q6?.applicationLevel?.value;
        }
        if (step >= 8 && step <= 10 && job?.q7?.manualEffort?.value) {
          tempObj.q7 = job?.q7?.manualEffort?.value;
        }
        if (step >= 9 && step <= 10 && job?.q8?.outcomes?.value) {
          tempObj.q8 = job?.q8?.outcomes?.value;
        }
        if (step === 10 && job?.q9?.notes) {
          let code = `IC | ${job?.q1?.output} - ${job?.q2?.institutionalExpertise?.level}${job?.q3?.culturalImpact?.level} - ${job?.q4?.physicalEnvironment?.level}${job?.q5?.mentalEnvironment?.level} - ${job?.q6?.applicationLevel?.level} - ${job?.q7?.manualEffort?.level} - ${job?.q8?.outcomes?.level}`;
          let q1 = Number(job?.q1?.output);
          let q2 = getPercentagefromString(job?.q2?.output);
          let q3 = getPercentagefromString(job?.q3?.output);
          let q4 = Number(job?.q4?.output);
          let q5 = Number(job?.q5?.output);
          let q6 = getPercentagefromString(job?.q6?.output);
          let q7 = Number(job?.q7?.output);
          let q8 = getPercentagefromString(job?.q8?.output);
          let result = ((q1 * q2 * q3 + (q4 + q5)) * q6 + q7) * q8;
          tempObj.q9 = job?.q9?.notes;
          tempObj.shortCode = code;
          tempObj.rasScore = Math.trunc(result);
          tempObj.rasLevel = 7;
        }
        if (job?.jobCode) {
          tempObj.jobCode = job?.jobCode;
        }
        if (job?.jobGrade) {
          tempObj.jobGrade = job?.jobGrade;
        }
        tempData.push(tempObj);
      } else {
        let tempObj: any = {
          jobTitle: job?.jobTitle,
          department: job?.department,
          roleType: job?.roleType?.value,
        };
        if (step >= 2 && step <= 10 && job?.q1) {
          if (job?.q1?.modifier?.value) {
            tempObj.modifier = job?.q1?.modifier?.value;
          }
          if (job?.q1?.levelWork?.value) {
            tempObj.levelOfWork = job?.q1?.levelWork?.value;
          }
        }
        if (step >= 3 && step <= 10 && job?.q2?.institutionalExpertise?.value) {
          tempObj.q2 = job?.q2?.institutionalExpertise?.value;
        }
        if (step >= 4 && step <= 10 && job?.q3?.culturalImpact?.value) {
          tempObj.q3 = job?.q3?.culturalImpact?.value;
        }
        if (step >= 5 && step <= 10 && job?.q4?.physicalEnvironment?.value) {
          tempObj.q4 = job?.q4?.physicalEnvironment?.value;
        }
        if (step >= 6 && step <= 10 && job?.q5?.mentalEnvironment?.value) {
          tempObj.q5 = job?.q5?.mentalEnvironment?.value;
        }
        if (step >= 7 && step <= 10 && job?.q6?.applicationLevel?.value) {
          tempObj.q6 = job?.q6?.applicationLevel?.value;
        }
        if (step >= 8 && step <= 10 && job?.q7?.manualEffort?.value) {
          tempObj.q7 = job?.q7?.manualEffort?.value;
        }
        if (step >= 9 && step <= 10 && job?.q8?.outcomes?.value) {
          tempObj.q8 = job?.q8?.outcomes?.value;
        }
        if (step === 10 && job?.q9?.notes) {
          let code = `M | ${job?.q1?.output} - ${job?.q2?.institutionalExpertise?.level}${job?.q3?.culturalImpact?.level} - ${job?.q4?.physicalEnvironment?.level}${job?.q5?.mentalEnvironment?.level} - ${job?.q6?.applicationLevel?.level} - ${job?.q7?.manualEffort?.level} - ${job?.q8?.outcomes?.level}`;
          let q1 = Number(job?.q1?.output);
          let q2 = getPercentagefromString(job?.q2?.output);
          let q3 = getPercentagefromString(job?.q3?.output);
          let q4 = Number(job?.q4?.output);
          let q5 = Number(job?.q5?.output);
          let q6 = getPercentagefromString(job?.q6?.output);
          let q7 = Number(job?.q7?.output);
          let response = await GetAppOutcomesService({
            level: job?.q6?.applicationLevel?.level,
            outcome: job?.q8?.outcomes?.level,
          });
          let q8 = getPercentagefromString(response?.data?.outcome);
          let result = ((q1 * q2 * q3 + (q4 + q5)) * q6 + q7) * q8;
          tempObj.q9 = job?.q9?.notes;
          tempObj.shortCode = code;
          tempObj.rasScore = Math.trunc(result);
          tempObj.rasLevel = 7;
        }
        if (job?.jobCode) {
          tempObj.jobCode = job?.jobCode;
        }
        if (job?.jobGrade) {
          tempObj.jobGrade = job?.jobGrade;
        }
        tempData.push(tempObj);
      }
      if (i === finalArr.length - 1) {
        finalDraftPayload.current = {
          evals: tempData,
        };
        AddDraftEvaluationsService(tempData[0], String(params?.id))
          .then(() => {
            toastMessage("success", "Evaluation saved as draft");
          })
          .catch((err) => {
            toastMessage("error", err?.response?.data?.error);
          })
          .finally(() => {
            setDraftLoading(false);
          });
      }
    }
  };

  const handleDraftSubmission = (finalArr: any) => {
    handleDaftFormData(finalArr);
  };

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    handleGetQuestions();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleGetEvaluations();
    // eslint-disable-next-line
  }, [params?.id]);

  return (
    <section
      className={classNames(
        "relative h-full flex flex-col justify-between gap-4"
      )}
    >
      <div className={classNames(styles.mainContainer)}>
        {step === 1 ? (
          <QuestionnaireStep1
            formikRef={formikRef}
            formData={localFormData}
            isEdit
            evalsLoading={evalsLoading}
            initialFormData={initialFormData.current}
          />
        ) : step === 2 ? (
          <QuestionnaireStep2
            formikRef={formikRef}
            formData={localFormData}
            setStep={setStep}
            setLoading={setLoading}
            setFormData={handleSetFormData}
            stepRef={stepRef}
          />
        ) : step === 3 ? (
          <QuestionnaireStep3 formikRef={formikRef} formData={localFormData} />
        ) : step === 4 ? (
          <QuestionnaireStep4 formikRef={formikRef} formData={localFormData} />
        ) : step === 5 ? (
          <QuestionnaireStep5 formikRef={formikRef} formData={localFormData} />
        ) : step === 6 ? (
          <QuestionnaireStep6 formikRef={formikRef} formData={localFormData} />
        ) : step === 7 ? (
          <QuestionnaireStep7 formikRef={formikRef} formData={localFormData} />
        ) : step === 8 ? (
          <QuestionnaireStep8 formikRef={formikRef} formData={localFormData} />
        ) : step === 9 ? (
          <QuestionnaireStep9
            formikRef={formikRef}
            formData={localFormData}
            setLoading={setLoading}
          />
        ) : step === 10 ? (
          <QuestionnaireStep10 formikRef={formikRef} formData={localFormData} />
        ) : step === 11 ? (
          <ReviewStep
            formData={localFormData}
            setStep={setStep}
            finalPayload={finalPayload}
            setLoading={setLoading}
          />
        ) : null}
      </div>

      <div className={classNames(styles.actionsContainer)}>
        {step > 1 ? (
          <CustomButton
            Icon={Icons.ArrowLeft}
            IconDirction="left"
            containerStyle={classNames(styles.prevButton)}
            onClick={() => {
              if (step > 1) {
                stepRef.current = step - 1;
                setStep(step - 1);
              }
            }}
            disabled={step === 1 || loading || evalsLoading}
          />
        ) : null}
        <div className={classNames("w-full flex items-center justify-center")}>
          <CustomPagination
            currentPage={step}
            totalCount={10}
            pageSize={1}
            displayArrow={false}
            disabled={loading}
            onPageChange={(page: any) => {
              if (!loading && !evalsLoading) {
                if (page < step) {
                  stepRef.current = page;
                  setStep(page);
                } else if (page > step) {
                  formikRef.current.submitForm();
                  //immediate next page
                  setTimeout(() => {
                    if (
                      formikRef.current?.errors &&
                      // eslint-disable-next-line
                      Object.keys(formikRef.current.errors).length == 0 &&
                      !loadingRef.current
                    ) {
                      if (
                        checkIfWarning(formikRef.current.values?.jobs, step)
                      ) {
                        if (step !== 2) {
                          let finalArr = mergeArrays(
                            stepRef.current,
                            localFormData,
                            formikRef.current.values?.jobs
                          );
                          if (
                            checkDropdownOptions(finalArr) ||
                            page === step + 1
                          ) {
                            setLocalFormData(finalArr);
                            stepRef.current = page;
                            dispatch(
                              setQuestionnaireReducer({
                                formData: finalArr,
                                lastStep: page,
                              })
                            );
                            setStep(page);
                          }
                        }
                      } else {
                        handleShowConfirmationModal();
                      }
                    }
                  }, 200);
                }
              }
            }}
          />
        </div>
        <div className={classNames("flex items-center gap-2")}>
          <CustomButton
            title={step === 10 ? "Review" : step === 11 ? "Submit" : "Continue"}
            Icon={step !== 11 ? Icons.ArrowRight : null}
            IconDirction="right"
            onClick={() => {
              if (step === 11) {
                if (checkIfComplete(formData)) {
                  handleSubmission();
                } else {
                  toastMessage("error", "Please fill all the fields");
                }
              } else {
                formikRef.current.submitForm();
                setTimeout(() => {
                  if (
                    formikRef.current?.errors &&
                    // eslint-disable-next-line
                    Object.keys(formikRef.current.errors).length == 0 &&
                    !loadingRef.current
                  ) {
                    if (checkIfWarning(formikRef.current.values?.jobs, step)) {
                      let finalArr = mergeArrays(
                        step,
                        localFormData,
                        formikRef.current.values?.jobs
                      );
                      stepRef.current = step + 1;
                      setLocalFormData(finalArr);
                      dispatch(
                        setQuestionnaireReducer({
                          formData: finalArr,
                          lastStep: stepRef.current,
                        })
                      );
                      if (step < 11) {
                        setStep(step + 1);
                      }
                    } else {
                      handleShowConfirmationModal();
                    }
                  }
                }, 200);
              }
            }}
            containerStyle={classNames(styles.nextButton)}
            disabled={loading || evalsLoading}
            loading={loading}
          />
          {step > 1 && step < 10 ? (
            <CustomButton
              title="Save"
              loading={draftLoading}
              onClick={() => {
                let finalArr = mergeArrays(
                  step,
                  localFormData,
                  formikRef.current.values?.jobs
                );
                setLocalFormData(finalArr);
                dispatch(
                  setQuestionnaireReducer({
                    formData: finalArr,
                    lastStep: stepRef.current,
                  })
                );
                handleDraftSubmission(finalArr);
              }}
              disabled={loading || draftLoading || evalsLoading}
              containerStyle={classNames(styles.saveDraftButton)}
            />
          ) : null}
        </div>
      </div>
      <SuccessModal
        title="Successful Submission"
        subtitle="Your evaluation has been completed"
        actionButtonText="Ok"
        show={showSuccessModal}
        handleClose={handleCloaseSuccessModal}
      />
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        actionButtonText="Yes, Continue"
        cancelButtonText="No, Cancel"
        title="Improbable combination found!"
        subtitle="Are you sure you want to proceed with this improbable combination?"
        handleSubmit={() => {
          let finalArr = mergeArrays(
            step,
            localFormData,
            formikRef.current.values?.jobs
          );
          stepRef.current = step + 1;
          setLocalFormData(finalArr);
          dispatch(
            setQuestionnaireReducer({
              formData: finalArr,
              lastStep: stepRef.current,
            })
          );
          if (step < 11) {
            setStep(step + 1);
          }
          handleCloseConfirmationModal();
        }}
      />
    </section>
  );
};

export default ContinueQuestionnaire;
